import {
  NavigateOptions,
  RegisteredRouter,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router"
import { useCallback } from "react"

import {
  ScopedParamsProp,
  ScopedPath,
  scopedTo,
} from "@/utilities/scopedRoutes"

import { getQrLookupIDFromRoute } from "./useTheme"

/**
 * A destination in both trees: the path, plus whatever params it declares.
 * `qrLookupID` is filled in from the current route, never passed here.
 */
type ScopedToOptions<T extends ScopedPath> = { to: T } & ScopedParamsProp<T>

/**
 * `navigate`'s options for the target path, so `search` is checked against
 * that route's schema rather than the union of every route's.
 */
type ScopedNavigateOptions<T extends ScopedPath> = Omit<
  NavigateOptions<RegisteredRouter, string, T>,
  "to" | "params"
> &
  ScopedToOptions<T>

/**
 * The generic is resolved at each call site, which is where the path, params
 * and search are checked. Reading them back out for the `scopedTo` call needs
 * a widening the caller never sees.
 */
type ResolvedScopedOptions = {
  to: ScopedPath
  params?: Record<string, string>
}

/**
 * The `qrLookupID` in play for the current route, from wherever it lives: the
 * path param inside the `/qr/$qrLookupID` tree, or search elsewhere (the
 * `/auth` tree carries it that way).
 */
function useCurrentQrLookupID() {
  const params = useParams({ strict: false })
  const search = useSearch({ strict: false })
  return getQrLookupIDFromRoute({ params, search }) || undefined
}

/**
 * `navigate` for paths that exist in both route trees. Targets the
 * `/qr/$qrLookupID` mirror when a `qrLookupID` is in play, and the top-level
 * path otherwise, so callers write the path once:
 *
 *     const scopedNavigate = useScopedNavigate()
 *     scopedNavigate({ to: "/account" })
 */
export function useScopedNavigate() {
  const navigate = useNavigate()
  const qrLookupID = useCurrentQrLookupID()

  return useCallback(
    <T extends ScopedPath>(options: ScopedNavigateOptions<T>) => {
      const { to, params, ...rest } = options as ResolvedScopedOptions &
        NavigateOptions

      return navigate({
        ...rest,
        ...scopedTo(to, qrLookupID, params as never),
      } as NavigateOptions)
    },
    [navigate, qrLookupID]
  )
}

/**
 * `scopedTo` bound to the `qrLookupID` in play, for destinations that aren't a
 * `navigate` call — `<Link>`, `<LinkButton>`, `buildLocation`, `matchRoute`:
 *
 *     const scoped = useScopedTo()
 *     <Link {...scoped({ to: "/account" })}>Account</Link>
 *
 * Components that decide for themselves which `qrLookupID` applies — or that
 * one doesn't — call `scopedTo` directly with it instead.
 */
export function useScopedTo() {
  const qrLookupID = useCurrentQrLookupID()

  return useCallback(
    <T extends ScopedPath>(options: ScopedToOptions<T>) => {
      const { to, params } = options as ResolvedScopedOptions

      return scopedTo(to, qrLookupID, params as never)
    },
    [qrLookupID]
  )
}
